import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Blocks, MessageSquare, CheckCircle2, AlertTriangle, ExternalLink, Save, Receipt, Ticket } from 'lucide-react';
import api from '@/lib/api';
import { openAxissAdmin } from '@/lib/axiss-sso';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface AxissStatus {
  enabled: boolean;
  configured: boolean;
  apiUrl: string | null;
  tenantId: string | null;
  enabledAt: string | null;
}

interface EnableResult {
  status?: 'minted' | 'reused' | 'already_provisioned';
  enabled?: boolean;
  configured?: boolean;
}

interface EtaxStatus {
  enabled: boolean;
  configured: boolean;
  apiBaseUrl: string | null;
  connectedAt: string | null;
}

interface TicketmasterStatus {
  enabled: boolean;
  configured: boolean;
  connectedAt: string | null;
  envFallback: boolean;
}

const bakedKey = (import.meta.env.VITE_AXISS_API_KEY as string) || '';

/**
 * Admin → Integrations
 *
 * One-click enable/disable for the AXISS support widget. Enabling provisions a
 * tenant + widget key on the AXISS backend and stores it server-side; the
 * widget then loads at runtime (no rebuild). The key is publishable and
 * origin-scoped, so it is served to the public site at page load.
 */
export default function LegacyIntegrations() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<AxissStatus | null>(null);
  const [busy, setBusy] = useState<'enable' | 'disable' | 'connect' | null>(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [manualKey, setManualKey] = useState('');

  const [etax, setEtax] = useState<EtaxStatus | null>(null);
  const [etaxBusy, setEtaxBusy] = useState<'connect' | 'enable' | 'disable' | null>(null);
  const [etaxKey, setEtaxKey] = useState('');

  const [tm, setTm] = useState<TicketmasterStatus | null>(null);
  const [tmBusy, setTmBusy] = useState<'connect' | 'enable' | 'disable' | null>(null);
  const [tmKey, setTmKey] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [axissStatus, etaxStatus, tmStatus] = await Promise.all([
        api.get<AxissStatus>('/admin/integrations/axiss'),
        api.get<EtaxStatus>('/admin/integrations/etax').catch(() => null),
        api.get<TicketmasterStatus>('/admin/integrations/ticketmaster').catch(() => null),
      ]);
      setStatus(axissStatus);
      setEtax(etaxStatus);
      setTm(tmStatus);
    } catch (err: any) {
      setError(err?.message || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function flash(msg: string) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  }

  async function enable() {
    setBusy('enable');
    setError('');
    try {
      const res = await api.post<EnableResult>('/admin/integrations/axiss/enable');
      if (res.status === 'already_provisioned') {
        // AXISS already has a key it won't return. If this site was provisioned
        // via the build-time flow, connect that baked key transparently.
        if (bakedKey) {
          await api.post('/admin/integrations/axiss/connect', { apiKey: bakedKey });
          flash('AXISS enabled (connected your existing widget key).');
        } else {
          setShowManual(true);
          setError('This site already has an AXISS key that can\'t be retrieved automatically. Paste it below to connect.');
          return;
        }
      } else {
        flash('AXISS enabled.');
      }
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to enable AXISS');
    } finally {
      setBusy(null);
    }
  }

  async function connectManual() {
    const key = manualKey.trim();
    if (!key) return;
    setBusy('connect');
    setError('');
    try {
      await api.post('/admin/integrations/axiss/connect', { apiKey: key });
      setManualKey('');
      setShowManual(false);
      flash('AXISS connected.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to connect AXISS key');
    } finally {
      setBusy(null);
    }
  }

  async function disable() {
    if (!confirm('Turn off the AXISS support widget on your site? You can re-enable it instantly.')) return;
    setBusy('disable');
    setError('');
    try {
      await api.post('/admin/integrations/axiss/disable');
      flash('AXISS disabled.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to disable AXISS');
    } finally {
      setBusy(null);
    }
  }

  async function connectEtax() {
    const key = etaxKey.trim();
    if (key.length < 8) {
      setError('Enter a valid ETaxEngine API key.');
      return;
    }
    setEtaxBusy('connect');
    setError('');
    try {
      await api.post('/admin/integrations/etax/connect', { apiKey: key });
      setEtaxKey('');
      flash('ETaxEngine connected.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to connect ETaxEngine');
    } finally {
      setEtaxBusy(null);
    }
  }

  async function enableEtax() {
    setEtaxBusy('enable');
    setError('');
    try {
      await api.post('/admin/integrations/etax/enable');
      flash('ETaxEngine enabled.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to enable ETaxEngine');
    } finally {
      setEtaxBusy(null);
    }
  }

  async function disableEtax() {
    if (!confirm('Turn off customs classification on your site? You can re-enable it instantly.')) return;
    setEtaxBusy('disable');
    setError('');
    try {
      await api.post('/admin/integrations/etax/disable');
      flash('ETaxEngine disabled.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to disable ETaxEngine');
    } finally {
      setEtaxBusy(null);
    }
  }

  async function connectTm() {
    const key = tmKey.trim();
    if (key.length < 8) {
      setError('Enter a valid Ticketmaster API key.');
      return;
    }
    setTmBusy('connect');
    setError('');
    try {
      await api.post('/admin/integrations/ticketmaster/connect', { apiKey: key });
      setTmKey('');
      flash('Ticketmaster connected — key validated against the Discovery API.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to connect Ticketmaster');
    } finally {
      setTmBusy(null);
    }
  }

  async function enableTm() {
    setTmBusy('enable');
    setError('');
    try {
      await api.post('/admin/integrations/ticketmaster/enable');
      flash('Ticketmaster enabled.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to enable Ticketmaster');
    } finally {
      setTmBusy(null);
    }
  }

  async function disableTm() {
    if (!confirm('Turn off Ticketmaster event discovery? Addie falls back to web research only. You can re-enable instantly.')) return;
    setTmBusy('disable');
    setError('');
    try {
      await api.post('/admin/integrations/ticketmaster/disable');
      flash('Ticketmaster disabled.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to disable Ticketmaster');
    } finally {
      setTmBusy(null);
    }
  }

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const on = !!status?.enabled && !!status?.configured;
  const etaxOn = !!etax?.enabled && !!etax?.configured;
  const tmOn = !!tm?.enabled && !!tm?.configured;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 pt-4">
        <Blocks className="h-5 w-5 text-ink-soft" aria-hidden="true" />
        <div>
          <h2 className="text-lg font-semibold text-ink">Platform services</h2>
          <p className="text-sm text-ink-muted">Rumio-operated services with their own one-click setup.</p>
        </div>
      </div>

      {successMsg && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
          {successMsg}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-2 text-sm text-rose-800"
        >
          {error}
        </div>
      )}

      <Card>
        <div className="space-y-5 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <MessageSquare className="mt-0.5 h-6 w-6 text-stone-700" aria-hidden="true" />
              <div>
                <h2 className="text-lg font-semibold text-stone-900">AXISS Support Widget</h2>
                <p className="mt-1 max-w-prose text-sm text-stone-600">
                  Add an AI-powered support chat bubble to your site — tickets,
                  FAQs, and a knowledge base, hosted by{' '}
                  <a
                    href="https://axiss.support"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-blue-700 underline"
                  >
                    AXISS
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                  . One click — no rebuild required.
                </p>
              </div>
            </div>
            <span
              className={
                'shrink-0 rounded-full px-3 py-1 text-xs font-medium ' +
                (on ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-600')
              }
            >
              {on ? 'Enabled' : 'Off'}
            </span>
          </div>

          {on ? (
            <div className="flex gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-emerald-900">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <div className="text-sm">
                <p className="font-medium">The support widget is live on your site.</p>
                <p className="mt-1">
                  Manage tickets, FAQs, and AI replies in the{' '}
                  <button
                    type="button"
                    onClick={() => void openAxissAdmin()}
                    className="inline-flex items-center gap-0.5 underline"
                  >
                    AXISS dashboard
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </button>
                  .
                  {status?.tenantId ? <span className="ml-1 text-emerald-700">Tenant {status.tenantId}.</span> : null}
                </p>
              </div>
            </div>
          ) : (
            !showManual &&
            bakedKey && (
              <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <div className="text-sm">
                  <p className="font-medium">A widget key is baked into your build.</p>
                  <p className="mt-1">
                    Click Enable to manage it here — that lets you turn the widget
                    on or off instantly without a redeploy.
                  </p>
                </div>
              </div>
            )
          )}

          <div className="flex flex-wrap items-center gap-3">
            {on ? (
              <Button onClick={disable} isLoading={busy === 'disable'} variant="secondary" size="md">
                Disable AXISS
              </Button>
            ) : (
              <Button onClick={enable} isLoading={busy === 'enable'} size="md">
                Enable AXISS
              </Button>
            )}
            {!on && !showManual && (
              <button
                type="button"
                onClick={() => setShowManual(true)}
                className="text-sm text-stone-500 underline hover:text-stone-700"
              >
                Paste an existing key
              </button>
            )}
          </div>

          {showManual && (
            <div className="space-y-2 border-t border-stone-200 pt-4">
              <label className="block text-sm font-medium text-stone-700" htmlFor="axiss-manual-key">
                AXISS widget key
              </label>
              <div className="flex gap-2">
                <Input
                  id="axiss-manual-key"
                  type="text"
                  value={manualKey}
                  onChange={(e) => setManualKey(e.target.value)}
                  placeholder="ax_live_…"
                  className="font-mono"
                />
                <Button onClick={connectManual} isLoading={busy === 'connect'} disabled={!manualKey.trim()} size="md">
                  <Save className="mr-1 h-4 w-4" aria-hidden="true" />
                  Connect
                </Button>
              </div>
              <p className="text-xs text-stone-500">
                Find or rotate your key in the AXISS dashboard → Account → API keys.
                The widget key is publishable (origin-scoped), so it's safe to embed.
              </p>
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="space-y-5 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Receipt className="mt-0.5 h-6 w-6 text-stone-700" aria-hidden="true" />
              <div>
                <h2 className="text-lg font-semibold text-stone-900">ETaxEngine Customs Classification</h2>
                <p className="mt-1 max-w-prose text-sm text-stone-600">
                  Upload supplier invoices and get AI-suggested HS / HTS tariff
                  codes you can apply to your products, powered by{' '}
                  <a
                    href="https://etaxengine.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-blue-700 underline"
                  >
                    ETaxEngine
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                  .
                </p>
              </div>
            </div>
            <span
              className={
                'shrink-0 rounded-full px-3 py-1 text-xs font-medium ' +
                (etaxOn ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-600')
              }
            >
              {etaxOn ? 'Enabled' : 'Off'}
            </span>
          </div>

          {etaxOn ? (
            <div className="flex gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-emerald-900">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <div className="text-sm">
                <p className="font-medium">Customs classification is active.</p>
                <p className="mt-1">
                  Upload invoices and apply tariff codes from{' '}
                  <Link to="/admin/customs" className="underline">
                    Customs
                  </Link>
                  .
                </p>
              </div>
            </div>
          ) : etax?.configured ? (
            <div className="flex gap-3 rounded-lg border border-stone-300 bg-stone-50 p-4 text-stone-700">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <div className="text-sm">
                <p className="font-medium">Your key is saved but the integration is off.</p>
                <p className="mt-1">Click Enable to turn customs classification back on.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <div className="text-sm">
                  <p className="font-medium">Connect your ETaxEngine account.</p>
                  <p className="mt-1">
                    Sign up at{' '}
                    <a
                      href="https://etaxengine.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 underline"
                    >
                      etaxengine.com
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                    , then paste your API key below (Account → API keys) to enable it here.
                  </p>
                </div>
              </div>
              <label className="block text-sm font-medium text-stone-700" htmlFor="etax-key">
                ETaxEngine API key
              </label>
              <div className="flex gap-2">
                <Input
                  id="etax-key"
                  type="password"
                  value={etaxKey}
                  onChange={(e) => setEtaxKey(e.target.value)}
                  placeholder="Paste your ETaxEngine API key"
                  className="font-mono"
                />
                <Button
                  onClick={connectEtax}
                  isLoading={etaxBusy === 'connect'}
                  disabled={etaxKey.trim().length < 8}
                  size="md"
                >
                  <Save className="mr-1 h-4 w-4" aria-hidden="true" />
                  Connect
                </Button>
              </div>
            </div>
          )}

          {(etaxOn || etax?.configured) && (
            <div className="flex flex-wrap items-center gap-3">
              {etaxOn ? (
                <Button onClick={disableEtax} isLoading={etaxBusy === 'disable'} variant="secondary" size="md">
                  Disable ETaxEngine
                </Button>
              ) : (
                <Button onClick={enableEtax} isLoading={etaxBusy === 'enable'} size="md">
                  Enable ETaxEngine
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card>
        <div className="space-y-5 p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Ticket className="mt-0.5 h-6 w-6 text-stone-700" aria-hidden="true" />
              <div>
                <h2 className="text-lg font-semibold text-stone-900">Ticketmaster Event Discovery</h2>
                <p className="mt-1 max-w-prose text-sm text-stone-600">
                  Give Addie real ticketed events (concerts, sports, theatre) near your properties for
                  the Local Guide and event ad campaigns — structured data with exact dates and venue
                  coordinates, powered by the free{' '}
                  <a
                    href="https://developer.ticketmaster.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-blue-700 underline"
                  >
                    Ticketmaster Discovery API
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                  . Web research still covers festivals and community events either way.
                </p>
              </div>
            </div>
            <span
              className={
                'shrink-0 rounded-full px-3 py-1 text-xs font-medium ' +
                (tmOn ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-600')
              }
            >
              {tmOn ? 'Enabled' : 'Off'}
            </span>
          </div>

          {tmOn ? (
            <div className="flex gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-emerald-900">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <div className="text-sm">
                <p className="font-medium">Ticketmaster discovery is active.</p>
                <p className="mt-1">
                  Addie merges Ticketmaster with web research on every event discovery run — see{' '}
                  <Link to="/admin/advertising/local-events" className="underline">
                    Local Events
                  </Link>
                  .
                </p>
              </div>
            </div>
          ) : tm?.configured ? (
            <div className="flex gap-3 rounded-lg border border-stone-300 bg-stone-50 p-4 text-stone-700">
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <div className="text-sm">
                <p className="font-medium">Your key is saved but the integration is off.</p>
                <p className="mt-1">Click Enable to turn Ticketmaster discovery back on.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {tm?.envFallback && (
                <div className="flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
                  <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
                  <div className="text-sm">
                    <p className="font-medium">A server-configured key is in use.</p>
                    <p className="mt-1">
                      Connecting a key here takes precedence and lets you manage it without a redeploy.
                    </p>
                  </div>
                </div>
              )}
              <label className="block text-sm font-medium text-stone-700" htmlFor="tm-key">
                Ticketmaster API key
              </label>
              <div className="flex gap-2">
                <Input
                  id="tm-key"
                  type="password"
                  value={tmKey}
                  onChange={(e) => setTmKey(e.target.value)}
                  placeholder="Paste your Consumer Key"
                  className="font-mono"
                />
                <Button
                  onClick={connectTm}
                  isLoading={tmBusy === 'connect'}
                  disabled={tmKey.trim().length < 8}
                  size="md"
                >
                  <Save className="mr-1 h-4 w-4" aria-hidden="true" />
                  Connect
                </Button>
              </div>
              <p className="text-xs text-stone-500">
                Free at developer.ticketmaster.com → My Apps → your app's Consumer Key. The key is
                validated live and stored server-side — it is never shown again.
              </p>
            </div>
          )}

          {(tmOn || tm?.configured) && (
            <div className="flex flex-wrap items-center gap-3">
              {tmOn ? (
                <Button onClick={disableTm} isLoading={tmBusy === 'disable'} variant="secondary" size="md">
                  Disable Ticketmaster
                </Button>
              ) : (
                <Button onClick={enableTm} isLoading={tmBusy === 'enable'} size="md">
                  Enable Ticketmaster
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
